// External Collection → Notifications — staff sent-history list. Routes:
//   /admin/notifications        → sent history (this page)
//   /admin/notifications/new    → compose
//   /admin/notifications/:id    → detail
// Gated by Portal.ManageAccounts (whoever manages external accounts can
// broadcast to them).

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useDateFormatter } from "@/utils/datetime";
import {
  adminNotificationsApi,
  type PortalNotificationAdminDto,
} from "@/api/adminNotifications";

const PAGE_SIZE = 50;

export function NotificationsAdminPage() {
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Notifications" },
  ]);
  const fmt = useDateFormatter();
  const [page, setPage] = useState(1);

  // The page rides the query key so each page is cached separately; the prefix
  // stays ["admin-notifications", …] so the compose page's invalidation of
  // that prefix still refreshes every cached page.
  const list = useQuery({
    queryKey: ["admin-notifications", page],
    queryFn: () =>
      adminNotificationsApi.list({
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
  });

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns: Column<PortalNotificationAdminDto>[] = [
    {
      key: "title",
      header: "Notification",
      render: (n) => (
        <Link
          to={`/admin/notifications/${n.id}`}
          className="font-semibold text-onyx hover:text-gold-shadow"
        >
          <div>{n.title}</div>
          <div className="text-caption text-gray-mid font-normal max-w-md truncate">
            {n.body}
          </div>
        </Link>
      ),
    },
    {
      key: "audience",
      header: "Audience",
      render: (n) => <span className="text-small text-onyx">{n.audienceSummary}</span>,
    },
    {
      key: "email",
      header: "Email",
      render: (n) =>
        n.emailSent ? (
          <Badge status="info">Emailed</Badge>
        ) : (
          <span className="text-caption text-gray-mid italic">In-portal only</span>
        ),
    },
    {
      key: "read",
      header: "Read",
      render: (n) => (
        <span className="text-small text-onyx">
          {n.readStats.read} of {n.readStats.total} read
        </span>
      ),
    },
    {
      key: "sender",
      header: "Sent by",
      render: (n) => (
        <span className="text-small text-onyx">{n.senderName ?? "—"}</span>
      ),
    },
    {
      key: "sentAt",
      header: "Sent",
      render: (n) => (
        <span className="text-small text-onyx">{fmt.dateTime(n.sentAt)}</span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        title="Notifications"
        description="Broadcast a plain-text notification to external portal users — an individual account, a category (Brokers / Buyers / Sellers / Vendors), or everyone. Recipients see it in their portal feed; optionally they also get a heads-up email."
        actions={
          <Link to="/admin/notifications/new">
            <Button type="button">
              <Plus size={16} className="mr-space-1" /> New notification
            </Button>
          </Link>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(n) => n.id}
        loading={list.isLoading}
        error={list.error instanceof Error ? list.error.message : null}
        emptyMessage="No notifications sent yet. Click 'New notification' to compose one."
      />

      {total > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={total}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </div>
  );
}
